import Alert from "@mui/material/Alert"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import type { MobilePlatform } from "@models/mobile_app_version"
import useTranslate from "@hooks/use_translate"

/**
 * Last gate before a write that can lock every mobile user out of the app.
 *
 * The server already rejects `min > latest`, but it cannot detect the real
 * hazard: a `minSupportedVersion` pointing at a build that is not live on the
 * store yet. That is a human judgement, so this dialog spells out the blast
 * radius and requires the operator to type the exact version — the GitHub
 * repo-delete pattern, and far stronger than the `window.confirm` used elsewhere
 * in this panel.
 */
export default function ConfirmForceUpdate({
  isOpen,
  setClose,
  loading,
  platform,
  savedMinVersion,
  nextMinVersion,
  savedLatestVersion,
  nextLatestVersion,
  onConfirm
}: {
  isOpen: boolean
  setClose: () => void
  loading?: boolean
  platform: MobilePlatform
  savedMinVersion: string
  nextMinVersion: string
  savedLatestVersion: string
  nextLatestVersion: string
  onConfirm: () => void
}) {
  const { translate } = useTranslate()
  const [typed, setTyped] = useState("")

  useEffect(() => {
    if (isOpen) setTyped("")
  }, [isOpen])

  const platformLabel = platform === "IOS" ? "iOS" : "Android"
  const confirmed = typed.trim() === nextMinVersion.trim()

  return (
    <DialogProvider
      title={translate("mobile_app_version_danger_title")}
      isOpen={isOpen}
      setClose={setClose}
      size="small"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("mobile_app_version_danger_confirm"),
        color: "error",
        onClick: onConfirm,
        loading,
        disabled: !confirmed || loading
      }}
    >
      <Stack gap={2}>
        <Alert severity="error">
          {translate("mobile_app_version_danger_body")
            .replace("{platform}", platformLabel)
            .replace("{version}", nextMinVersion)}
        </Alert>

        <Stack gap={0.5}>
          <Typography variant="body2">
            {translate("mobile_app_version_min_supported")}:{" "}
            <strong>
              {savedMinVersion || "—"} → {nextMinVersion}
            </strong>
          </Typography>
          <Typography variant="body2">
            {translate("mobile_app_version_latest")}:{" "}
            <strong>
              {savedLatestVersion || "—"} → {nextLatestVersion}
            </strong>
          </Typography>
        </Stack>

        <Alert severity="warning">
          {translate("mobile_app_version_danger_store_check")}
        </Alert>

        <TextField
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          disabled={loading}
          label={translate("mobile_app_version_danger_confirm_hint").replace(
            "{version}",
            nextMinVersion
          )}
          placeholder={nextMinVersion}
          size="small"
          fullWidth
        />
      </Stack>
    </DialogProvider>
  )
}
